import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { decamelizeKeys } from 'humps';
import type { AppAction, AppState, ThunkAction } from '../../types/app.types';
import type { AccountAddress, V2DeliverySummary, V2Pharmacy, V2Provider, V2Response } from '../../types/api-v2.types';
import type {
  DeliveryState,
  SetProfileForm,
  Profile,
  ClearProfileForm,
  ClearRxSources,
  SetRxSource,
  SetRxSourceActiveKey,
} from './delivery-module.types';
import formatDate from 'date-fns/format';
import { stringify } from 'query-string';
import { setAccountAddress } from './account-module';

export const isBlinkHealthPharmacy = (pharmacy: V2Pharmacy | null | undefined) =>
  pharmacy && pharmacy.location && pharmacy.location.id === '1063919710';
export const isAwaitingRx = (status: string | null | undefined) => status && status.match(/pending|transferring/g);

export const BHP: V2Pharmacy = {
  isSupersaver: false,
  isPreferred: false,
  brand: null,
  isDelivery: true,
  inNetwork: true,
  networkId: '3',
  name: 'Blink Health Pharmacy',
  npi: '1063919710',
  location: {
    address: {
      zipCode: '63005',
      state: 'MO',
      street2: 'Suite 268',
      street1: '100 Chesterfield Business Parkway',
      city: 'St Louis',
    },
    phone: '3143562943',
    coordinates: {
      lng: -90.6189799,
      lat: 38.6675451,
    },
    id: '1063919710',
    fax: '5005550006',
  },
};

export type MaybeAccountAddress = {
  address1?: string;
  address2?: string | null | undefined;
  city?: string;
  state?: string;
  zipCode?: string;
  isVerified?: boolean;
  source?: string;
  specialInstructions?: string | null | undefined;
};

const MILLISECONDS_IN_7_DAYS = 86400000 * 7;
export const getEstDeliveryDate = (format = 'MMMM Do') => formatDate(Date.now() + MILLISECONDS_IN_7_DAYS, format);
type ShortAddress = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
};

const addressesMatch = (address1: ShortAddress, address2: ShortAddress) =>
  address1 &&
  address2 &&
  ['street', 'city', 'state', 'zipCode'].every(
    (key) => address1[key] && address2[key] && address1[key].toLowerCase() === address2[key].toLowerCase()
  );

export function updateDeliveryAddress(address: AccountAddress): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const verifyArgs: any = {
      street: address.address1,
      street2: address.address2,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
    };
    return blinkAPIClient
      .get(`v2/account/address/verify?${stringify(decamelizeKeys(verifyArgs))}`)
      .then((response) => response.data)
      .then((data) => data.result)
      .then((result) => {
        if (result.suggestions) {
          const addresses = result.suggestions;
          const topSuggestion: ShortAddress = addresses[0];

          if (topSuggestion) {
            if (addressesMatch(verifyArgs, topSuggestion)) {
              return dispatch(
                setAccountAddress({
                  address1: topSuggestion.street,
                  address2: address.address2 || '',
                  city: topSuggestion.city,
                  state: topSuggestion.state,
                  zipCode: topSuggestion.zipCode,
                  isVerified: true,
                  specialInstructions: address.specialInstructions || null,
                })
              );
            }

            return {
              error: true,
              status: 409,
              results: addresses.map(({ street, city, state, zipCode }) => ({
                address1: street,
                city: city,
                state: state,
                zipCode: zipCode,
              })),
            };
          }
        }

        if (result.error) {
          return {
            error: true,
            status: 400,
            validationErrors: {
              shippingAddress: result.message,
            },
          };
        }

        return {
          error: true,
          status: 400,
          validationErrors: {
            shippingAddress:
              "We couldn't find that address in the USPS database. Double check the address you’ve entered is correct. For help, call 1 (844) 312-5905.",
          },
        };
      });
  };
}

export function requestDeliverySummary(orderId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`v2/deliveries/${orderId}`)
      .then((res) => res.data)
      .then((data: V2Response<V2DeliverySummary>): V2DeliverySummary => data.result)
      .then((res) =>
        dispatch({
          type: 'delivery/DELIVERY_SUMMARY_SUCCESS',
          res,
          orderId,
        })
      )
      .catch((error) =>
        dispatch({
          type: 'delivery/DELIVERY_SUMMARY_ERROR',
          ...error,
        })
      );
  };
}

export function setProfileForm(profileForm: Profile): SetProfileForm {
  return {
    type: 'delivery/SET_PROFILE_FORM',
    profileForm,
  };
}

export function clearProfileForm(): ClearProfileForm {
  return {
    type: 'delivery/CLEAR_PROFILE_FORM',
  };
}

const SEARCH_ITEMS_TO_SHOW = 4;
let allergySearchCanceller: CancelTokenSource;

export function allergySearch(search: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const opts = {
      search,
    };

    if (allergySearchCanceller) {
      allergySearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    allergySearchCanceller = axios.CancelToken.source();
    return blinkAPIClient
      .get(`/v2/allergens?${stringify(decamelizeKeys(opts))}`, {
        cancelToken: allergySearchCanceller.token,
      })
      .then((res) => res.data)
      .then((data: any) => data.result)
      .then((result) => result.allergens)
      .then((allergens) => ({
        results: allergens.slice(0, SEARCH_ITEMS_TO_SHOW),
      }))
      .catch(() => {
        // TODO: Handle error
      });
  };
}

let otherMedicationSearchCanceller: CancelTokenSource;

export function otherMedicationSearch(search: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const opts = {
      search,
    };

    if (otherMedicationSearchCanceller) {
      otherMedicationSearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    otherMedicationSearchCanceller = axios.CancelToken.source();
    return blinkAPIClient
      .get(`/v2/drugs/search/category/med_name?${stringify(decamelizeKeys(opts))}`, {
        cancelToken: otherMedicationSearchCanceller.token,
      })
      .then((res) => res.data)
      .then((data: V2Response<any>) => data.result)
      .then((otherMedications) => ({
        results: otherMedications.slice(0, SEARCH_ITEMS_TO_SHOW).map((med) => ({
          medNameId: med.medNameId,
          description: med.formattedName,
        })),
      }))
      .catch(() => {
        // TODO: Handle error
      });
  };
}

let medConditionSearchCanceller: CancelTokenSource;

export function medConditionSearch(search: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const opts = {
      search,
    };

    if (medConditionSearchCanceller) {
      medConditionSearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    medConditionSearchCanceller = axios.CancelToken.source();
    return blinkAPIClient
      .get(`/v2/medical_conditions?${stringify(decamelizeKeys(opts))}`, {
        cancelToken: medConditionSearchCanceller.token,
      })
      .then((res) => res.data)
      .then((data: V2Response<any>) => data.result)
      .then((result) => result.medicalConditions)
      .then((medicalConditions) => ({
        results: medicalConditions.slice(0, SEARCH_ITEMS_TO_SHOW),
      }))
      .catch(() => {
        // TODO: Handle error
      });
  };
}

let providerSearchCanceller: CancelTokenSource;

export function providerSearch(
  providerName: string,
  additionalParameters: Record<string, any> | null | undefined = {}
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const options = {
      search: providerName,
      ...additionalParameters,
    };

    if (providerSearchCanceller) {
      providerSearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    providerSearchCanceller = axios.CancelToken.source();
    return blinkAPIClient
      .get(`/v2/providers?${stringify(decamelizeKeys(options))}`, {
        cancelToken: providerSearchCanceller.token,
      })
      .then((res) => res.data)
      .then((data) => data.result)
      .then((result) => ({
        results: result.providers,
      }))
      .catch(() => {
        // TODO: Handle error
      });
  };
}

export function getProfile(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`v2/user/patient_profile`)
      .then((res) => res.data)
      .then((data: V2Response<Profile>): Profile => data.result)
      .then((res) =>
        dispatch({
          type: 'delivery/GET_PROFILE_SUCCESS',
          res,
        })
      )
      .catch((error) =>
        dispatch({
          type: 'delivery/GET_PROFILE_ERROR',
          ...error,
        })
      );
  };
}

export function updateProfile(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const profileForm = getState().delivery.profileForm;
    return blinkAPIClient
      .post(`v2/user/patient_profile`, {
        allergies: [],
        medicalConditions: [],
        otherMedications: [],
        ...profileForm,
      })
      .then((res) => res.data)
      .then((data: V2Response<Profile>): Profile => data.result)
      .then((res) =>
        dispatch({
          type: 'delivery/SET_PROFILE_SUCCESS',
          res,
        })
      )
      .catch((error) =>
        dispatch({
          type: 'delivery/SET_PROFILE_ERROR',
          ...error,
        })
      );
  };
}

export function clearRxSourceSelections(): ClearRxSources {
  return {
    type: 'delivery/CLEAR_RX_SOURCES',
  };
}

export function setRxSourceSelection({
  cartItemId,
  provider,
  pharmacy,
}: {
  cartItemId: string;
  provider?: V2Provider | null | undefined;
  pharmacy?: V2Pharmacy | null | undefined;
}): SetRxSource {
  return {
    type: 'delivery/SET_RX_SOURCE',
    cartItemId,
    provider,
    pharmacy,
  };
}

export function setRxSourceActiveKey(rxSourceActiveKey: string): SetRxSourceActiveKey {
  return {
    type: 'delivery/SET_RX_SOURCE_ACTIVE_KEY',
    rxSourceActiveKey,
  };
}

export const getDeliverySummaryFromState = (state: AppState, orderId: string): V2DeliverySummary | null | undefined => {
  return state.delivery.orderSummaries[orderId];
};
const initialState: DeliveryState = {
  shippingAddress: null,
  orderSummaries: {},
  rxSourceSelections: {},
  rxSourceActiveKey: 'all',
  profileForm: {},
  profile: null,
};
export default function deliveryReducer(state: DeliveryState = { ...initialState }, action: AppAction): DeliveryState {
  switch (action.type) {
    case 'account/GET_ADDRESS_SUCCESS': {
      const shippingAddress = action.data;
      if (shippingAddress && shippingAddress.isVerified) return { ...state, shippingAddress };
      return state;
    }

    case 'delivery/SET_PROFILE_SUCCESS':
    case 'delivery/GET_PROFILE_SUCCESS': {
      const profile = action.res;
      return { ...state, profile };
    }

    case 'delivery/SET_PROFILE_FORM': {
      const profileForm = action.profileForm;
      return { ...state, profileForm: { ...state.profileForm, ...profileForm } };
    }

    case 'delivery/CLEAR_PROFILE_FORM': {
      return { ...state, profileForm: {} };
    }

    case 'delivery/SET_RX_SOURCE_ACTIVE_KEY': {
      const { rxSourceActiveKey } = action;
      return { ...state, rxSourceActiveKey };
    }

    case 'delivery/SET_RX_SOURCE': {
      const { provider, pharmacy, cartItemId } = action;
      return {
        ...state,
        rxSourceSelections: {
          ...state.rxSourceSelections,
          [cartItemId]: {
            provider,
            pharmacy,
          },
        },
      };
    }

    case 'delivery/CLEAR_RX_SOURCES':
      return { ...state, rxSourceSelections: {} };

    case 'delivery/DELIVERY_SUMMARY_SUCCESS': {
      const { res, orderId } = action;
      return { ...state, orderSummaries: { ...state.orderSummaries, [orderId]: res } };
    }

    case 'search/SELECT_RESULT': {
      const data = action.data;
      const { allergies = [], medicalConditions = [], otherMedications = [] } = state.profileForm;
      return {
        ...state,
        rxSourceSelections:
          data.searchView === 'PHARMACY_SEARCH' || data.searchView === 'PROVIDER_SEARCH'
            ? {
                ...state.rxSourceSelections,
                [state.rxSourceActiveKey]: {
                  pharmacy: (data.searchView === 'PHARMACY_SEARCH' && data.result) || null,
                  provider: (data.searchView === 'PROVIDER_SEARCH' && data.result) || null,
                },
              }
            : state.rxSourceSelections,
        profileForm: {
          allergies: data.searchView === 'ALLERGY_SEARCH' ? [...allergies, data.result] : allergies,
          medicalConditions:
            data.searchView === 'MEDICAL_CONDITION_SEARCH' ? [...medicalConditions, data.result] : medicalConditions,
          otherMedications:
            data.searchView === 'OTHER_MEDICATION_SEARCH' ? [...otherMedications, data.result] : otherMedications,
        },
      };
    }

    default:
      return state;
  }
}
